import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TelephonyContext, TelephonyContextResponse } from 'src/app/models/telephonyv2';
import {
    IMemberSearchMetadataRequest,
    IMemberSearchMetadataResponse,
    IMemberSearchRequest,
    IMemberSearchResponse,
    IMemberSearchResponseFacetResult,
} from 'src/app/pages/_searchs/member/member-search.model';

import { IMedicaidEndDate } from 'src/app/models/medicaid';
import { MemberContext, MemberRequestItem, MemberSSNRequestItem } from '../../models/member';
import { ConfigurationService, ForceBaseUrl } from '../configuration/configuration.service';
import { EndpointFactory } from '../endpoint-factory.service';

@Injectable({
    providedIn: 'root'
})
export class MemberEndpoint extends EndpointFactory {

    constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {

        super(http, configurations, injector);
    }

    getServiceBaseUrl(force?: ForceBaseUrl) {
        return this.getBaseUrl('member', force) + 'member';
    }

    getTelephonyServiceBaseUrl(force?: ForceBaseUrl) {
        return this.getBaseUrl('telephony', force) + 'telephony';
    }

    getSearch(req: IMemberSearchRequest): Observable<IMemberSearchResponse> {
        const endpointUrl = `${this.getServiceBaseUrl()}/search`;

        return this.http.post<IMemberSearchResponse>(endpointUrl, req).pipe<IMemberSearchResponse>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getFacets(req: IMemberSearchRequest): Observable<IMemberSearchResponseFacetResult[]> {
        const endpointUrl = `${this.getServiceBaseUrl()}/search/facets`;
        return this.http.post<IMemberSearchResponseFacetResult[]>(endpointUrl, req).pipe<IMemberSearchResponseFacetResult[]>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getSearchMetadata(req: IMemberSearchMetadataRequest): Observable<IMemberSearchMetadataResponse> {
        const endpointUrl = `${this.getServiceBaseUrl()}/searchmetadata`;

        return this.http.post<IMemberSearchMetadataResponse>(endpointUrl, req).pipe<IMemberSearchMetadataResponse>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getMemberEndpoint(memberIdentifier: string, contextInput?: any): Observable<MemberContext> {
        const endpointUrl = `${this.getServiceBaseUrl('WEBAPI')}/${memberIdentifier}/get`;

        return this.http.post<MemberContext>(endpointUrl, contextInput || {}).pipe(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getMemberById<T>(memberIdentifier: string): Observable<T> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${memberIdentifier}/getmember`;

        return this.http.get<T>(endpointUrl).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getMemberSSN<T>(memberIdentifier: string): Observable<T> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${memberIdentifier}/getmember/ssn`;

        return this.http.get<T>(endpointUrl).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getFullMemberEndpoint<T>(memberIdentifier: string): Observable<T> {
        const endpointUrl = `${this.getServiceBaseUrl()}/full/${memberIdentifier}`;

        return this.http.get<T>(endpointUrl).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getMMREndpoint<T>(memberIdentifier: string): Observable<T> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${memberIdentifier}/mmr`;

        return this.http.get<T>(endpointUrl).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getMemberMedicaidDateRecords(tenantId: string, memberId: string, source: string): Observable<IMedicaidEndDate[]> {

        let endpointUrl =
            (source)
                ? `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/medicaid?userObjectId=${source}`
                : `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/medicaid`;

        return this.http.get<IMedicaidEndDate[]>(endpointUrl).pipe<IMedicaidEndDate[]>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    createMemberMedicaidDateRecords(tenantId: string, memberId: string, request: IMedicaidEndDate): Observable<IMedicaidEndDate> {

        let endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/enddate-create`;

        return this.http.post<IMedicaidEndDate>(endpointUrl, request).pipe<IMedicaidEndDate>(
            catchError(error => {
                return this.handleError(error);
            })
        );
    }

    updateMemberMedicaidDateRecords(tenantId: string, memberId: string, request: IMedicaidEndDate): Observable<IMedicaidEndDate> {

        let endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/enddate-update`;

        return this.http.post<IMedicaidEndDate>(endpointUrl, request).pipe<IMedicaidEndDate>(
            catchError(error => {
                return this.handleError(error);
            })
        );
    }

    deleteMemberMedicaidDateRecords(tenantId: string, id: number) {

        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${id}/enddate-delete`;

        return this.http.delete<boolean>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })
        );
    }

    updateMember(base64: string, member: MemberRequestItem): Observable<{ result: boolean }> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${base64}/update`;

        return this.http.put<{ result: boolean }>(endpointUrl, member).pipe(
            catchError(error => {
                return this.handleError(error);
            }));
    }
    updateMemberSSN(base64: string, member: MemberSSNRequestItem): Observable<boolean> {
        const endpointUrl = `${this.getServiceBaseUrl()}/${base64}/update/ssn`;

        return this.http.put<boolean>(endpointUrl, member).pipe(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    //#region Telephony
    recordCall(request: TelephonyContext): Observable<TelephonyContextResponse> {
        const endpointUrl = `${this.getTelephonyServiceBaseUrl('WEBAPI')}/${request.tenantId}/recordcall`;

        return this.http.post<TelephonyContextResponse>(endpointUrl, request).pipe(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getMemberSurveyRating(tenantId, memberId) {
        const endpointUrl = `${this.getTelephonyServiceBaseUrl('WEBAPI')}/${tenantId}/surveyRating/` + memberId;

        return lastValueFrom(this.http.get<any>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            }
            )));

    }
    //#endregion

}
